import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import {
  BuildingLibraryIcon,
  CheckIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  isRorOtherOption,
  ROR_OTHER_OPTION,
  searchRorInstitutions,
  type RorInstitution,
} from '@/services/rorService';

type RorInstitutionSelectProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  invalid?: boolean;
  /** Visual variant aligned with signup card or legacy form */
  variant?: 'signup' | 'form';
  placeholder?: string;
  disabled?: boolean;
};

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

export default function RorInstitutionSelect({
  value,
  onChange,
  onBlur,
  invalid = false,
  variant = 'signup',
  placeholder = 'Search university or research institute…',
  disabled = false,
}: RorInstitutionSelectProps) {
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<RorInstitution[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isOtherMode, setIsOtherMode] = useState(false);
  const [selectedFromList, setSelectedFromList] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const isSignup = variant === 'signup';
  const inputBaseClass = isSignup ? 'signup-form-input' : 'form-input';

  useEffect(() => {
    setQuery(value);
    if (!value) {
      setSelectedFromList(false);
    }
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const runSearch = useCallback(async (term: string) => {
    abortRef.current?.abort();
    if (term.trim().length < MIN_QUERY_LENGTH) {
      setResults([]);
      setIsLoading(false);
      setSearchError(null);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);
    setSearchError(null);

    try {
      const items = await searchRorInstitutions(term, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setResults(items);
      setHighlightedIndex(0);
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
      setResults([]);
      setSearchError('Could not load institutions. You can choose Other.');
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  }, []);

  const scheduleSearch = useCallback(
    (term: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void runSearch(term);
      }, DEBOUNCE_MS);
    },
    [runSearch],
  );

  const clearSelection = () => {
    setIsOtherMode(false);
    setSelectedFromList(false);
    setQuery('');
    onChange('');
    setResults([]);
    setIsOpen(false);
  };

  const selectInstitution = (institution: RorInstitution) => {
    if (isRorOtherOption(institution.id)) {
      setIsOtherMode(true);
      setSelectedFromList(false);
      setIsOpen(false);
      setResults([]);
      setQuery('');
      onChange('');
      return;
    }

    setIsOtherMode(false);
    setSelectedFromList(true);
    setQuery(institution.name);
    onChange(institution.name);
    setIsOpen(false);
    setResults([]);
  };

  const options: RorInstitution[] = [...results, ROR_OTHER_OPTION];

  const handleInputChange = (next: string) => {
    setSelectedFromList(false);
    setQuery(next);
    onChange(next);
    if (isOtherMode) return;
    setIsOpen(true);
    scheduleSearch(next);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (isOtherMode || !isOpen) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter' && highlightedIndex >= 0 && options[highlightedIndex]) {
      event.preventDefault();
      selectInstitution(options[highlightedIndex]);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const showDropdown = isOpen && !isOtherMode && query.trim().length >= MIN_QUERY_LENGTH;
  const showClear = Boolean(query) && !disabled;

  return (
    <div
      className={`ror-institution-select${isSignup ? ' ror-institution-select--signup' : ' ror-institution-select--form'}`}
      ref={containerRef}
    >
      {isOtherMode && (
        <div className="ror-institution-select__mode-chip">
          <span className="ror-institution-select__mode-chip-label">Other institution</span>
          <button type="button" className="ror-institution-select__mode-chip-action" onClick={clearSelection}>
            Back to search
          </button>
        </div>
      )}

      {selectedFromList && value && !isOtherMode && (
        <div className="ror-institution-select__selected" aria-live="polite">
          <BuildingLibraryIcon className="ror-institution-select__selected-icon" aria-hidden />
          <span className="ror-institution-select__selected-text">{value}</span>
          <button
            type="button"
            className="ror-institution-select__selected-clear"
            aria-label="Clear selected institution"
            onClick={clearSelection}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}

      {!(selectedFromList && value && !isOtherMode) && (
        <div className={`ror-institution-select__control${invalid ? ' ror-institution-select__control--invalid' : ''}`}>
          <MagnifyingGlassIcon className="ror-institution-select__leading-icon" aria-hidden />
          <input
            className={`${inputBaseClass} ror-institution-select__input${invalid ? ' signup-form-input--invalid' : ''}`}
            aria-label="organization"
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-expanded={showDropdown}
            role="combobox"
            type="text"
            autoComplete="off"
            placeholder={isOtherMode ? 'Type your institution name…' : placeholder}
            disabled={disabled}
            value={query}
            onChange={evt => handleInputChange(evt.target.value)}
            onFocus={() => {
              if (!isOtherMode && query.trim().length >= MIN_QUERY_LENGTH) {
                setIsOpen(true);
                scheduleSearch(query);
              }
            }}
            onBlur={() => {
              setTimeout(() => onBlur?.(), 150);
            }}
            onKeyDown={handleKeyDown}
          />
          <div className="ror-institution-select__trailing">
            {isLoading && <span className="ror-institution-select__spinner" aria-hidden />}
            {showClear && !isLoading && (
              <button
                type="button"
                className="ror-institution-select__icon-btn"
                aria-label="Clear"
                onMouseDown={evt => evt.preventDefault()}
                onClick={clearSelection}
              >
                <XMarkIcon className="h-4 w-4" aria-hidden />
              </button>
            )}
            {!isOtherMode && (
              <ChevronDownIcon
                className={`ror-institution-select__chevron${showDropdown ? ' ror-institution-select__chevron--open' : ''}`}
                aria-hidden
              />
            )}
          </div>
        </div>
      )}

      {showDropdown && (
        <ul id={listboxId} className="ror-institution-select__list" role="listbox">
          {isLoading && results.length === 0 && (
            <li className="ror-institution-select__status">Searching institutions…</li>
          )}
          {!isLoading && searchError && (
            <li className="ror-institution-select__status ror-institution-select__status--error">{searchError}</li>
          )}
          {!isLoading && !searchError && results.length === 0 && (
            <li className="ror-institution-select__status">No matches found. Choose Other below.</li>
          )}
          {options.map((item, index) => {
            const isOther = isRorOtherOption(item.id);
            const meta = [item.city, item.country].filter(Boolean).join(', ');
            const active = highlightedIndex === index;
            return (
              <li
                key={item.id}
                role="option"
                aria-selected={active}
                className={`ror-institution-select__option${active ? ' ror-institution-select__option--active' : ''}${
                  isOther ? ' ror-institution-select__option--other' : ''
                }`}
                onMouseDown={evt => {
                  evt.preventDefault();
                  selectInstitution(item);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <span className="ror-institution-select__option-icon" aria-hidden>
                  {isOther ? (
                    <span className="ror-institution-select__other-badge">+</span>
                  ) : (
                    <BuildingLibraryIcon className="h-4 w-4" />
                  )}
                </span>
                <span className="ror-institution-select__option-body">
                  <span className="ror-institution-select__option-name">{item.name}</span>
                  {meta && !isOther && (
                    <span className="ror-institution-select__option-meta">
                      <MapPinIcon className="h-3 w-3" aria-hidden />
                      {meta}
                    </span>
                  )}
                  {isOther && (
                    <span className="ror-institution-select__option-meta">Enter your institution manually</span>
                  )}
                </span>
                {active && !isOther && (
                  <CheckIcon className="ror-institution-select__option-check" aria-hidden />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!isOtherMode && !selectedFromList && query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH && (
        <p className="ror-institution-select__hint">Type at least 2 characters to search</p>
      )}
    </div>
  );
}
